import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/app-shell";
import { ExpiryDialog } from "@/components/expiry-dialog";
import { IdleDialog } from "@/components/idle-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { EXPIRY_WARNING_DAYS, daysUntilPasswordExpiry } from "@/lib/password-expiry";
import { effectivePolicyQuery, profileQuery } from "@/lib/queries";
import { useIdleTimeout } from "@/lib/use-idle-timeout";
import { authSnapshot, useAuthStore } from "@/stores/auth";
import { useTranslation } from "@/stores/locale";

export const Route = createFileRoute("/_app")({
  beforeLoad: ({ location }) => {
    if (!authSnapshot().accessToken) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  // Load the profile before the shell paints, so no page flashes without its
  // permissions and then rearranges itself. The caller's resolved policy —
  // lockout, password expiry, idle timeout, already walked from user through
  // group and organization — comes with it, so every page in here has it
  // without asking again. A deployment that does not serve it is not a reason
  // to hold up the app.
  loader: async ({ context, location }) => {
    const profile = await context.queryClient.ensureQueryData(profileQuery());
    // After the profile, not beside it. Launched together, both reach the
    // network before either can report the server missing — two failed
    // requests where one is enough to know, and the API client's door cannot
    // help because they start in the same tick. Once the profile has answered,
    // the server is known to be there and this costs the round trip it always
    // did; when it has not, this never leaves the browser.
    await context.queryClient.ensureQueryData(effectivePolicyQuery()).catch(() => undefined);

    // A forced change is a wall, not a suggestion: nothing else in here is
    // reachable until it is done. It cannot go in `beforeLoad` — the profile
    // that decides it is only fetched here.
    if (profile.force_password_change && location.pathname !== "/change-password") {
      throw redirect({ to: "/change-password" });
    }
    return profile;
  },
  component: AppLayout,
  pendingComponent: ShellSkeleton,
});

function AppLayout() {
  const t = useTranslation();
  const { data: profile } = useQuery(profileQuery());
  const { data: policy } = useQuery(effectivePolicyQuery());
  const navigate = useNavigate();
  const signOut = useAuthStore((state) => state.signOut);
  // "Remind me later" holds for as long as the shell stays mounted — so it
  // does not follow you from page to page — and a reload asks again. The
  // warning is the only thing standing between someone and an account they
  // cannot sign in to, so it is not something to dismiss once and forget.
  const [deferred, setDeferred] = useState(false);

  const handleSignOut = (message?: string) => {
    const { refreshToken } = authSnapshot();
    // Fire and forget: the local session ends either way, and a failed revoke
    // must not strand the person on a signed-in-looking screen.
    if (refreshToken) {
      void api.post("/auth/logout", { refresh_token: refreshToken }).catch(() => undefined);
    }
    signOut();
    if (message) toast.info(message);
    void navigate({ to: "/login" });
  };

  // The policy's own number, so a deployment, an organisation, a group or one
  // person can all set it. Unset means the session never times out.
  const idle = useIdleTimeout(policy?.idle_timeout_minutes, () =>
    handleSignOut(
      t("idle.signedOut").replace("{n}", String(policy?.idle_timeout_minutes ?? "")),
    ),
  );

  if (!profile) return <ShellSkeleton />;

  // The expiry is arithmetic on the policy's interval and the profile's last
  // change, so it is known on every load rather than only at sign-in.
  const remaining = daysUntilPasswordExpiry(profile, policy);
  const warn = !deferred && remaining !== null && remaining <= EXPIRY_WARNING_DAYS;

  return (
    <AppShell profile={profile} onSignOut={() => handleSignOut()}>
      {idle.secondsLeft === null ? null : (
        <IdleDialog secondsLeft={idle.secondsLeft} onStay={idle.stay} />
      )}
      {warn ? (
        <ExpiryDialog
          remaining={remaining}
          onLater={() => setDeferred(true)}
          onChange={() => void navigate({ to: "/change-password" })}
        />
      ) : null}
      <Outlet />
    </AppShell>
  );
}

/** The shell's own shape, held while the profile loads, so nothing jumps. */
function ShellSkeleton() {
  return (
    <div className="grid min-h-dvh md:grid-cols-[264px_1fr]">
      <div className="bg-sidebar border-sidebar-line hidden border-r px-3 py-4 md:block">
        <Skeleton className="mx-1 h-9 w-24" />
        <Skeleton className="mt-3 h-8 rounded-lg" />
        <div className="mt-6 grid gap-1.5">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-9 rounded-lg" />
          ))}
        </div>
      </div>
      <div>
        <div className="border-line h-[61px] border-b" />
        <div className="px-5 pt-6 md:px-8">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="mt-3 h-4 w-80" />
          <Skeleton className="rounded-card mt-6 h-96" />
        </div>
      </div>
    </div>
  );
}
